import { EntitlementSlugEnum } from '@electricjs/core_entity-client';
import {
  ModalV2,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalAction,
  Text,
  useBreakpoint,
  Skeleton,
  Flex,
  Tooltip,
  spaces,
  Box,
  TextMarkdown,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Store } from '@/types/storefront';
import { type Product } from '@/types/products';
import { StorefrontAsset } from '@/types/assets';
import { ELECTRIC_WAREHOUSE_SITE_NAME } from '@/constants/assets';
import { useStorefront } from '../StorefrontProvider';
import formatCurrency from '@/helpers/formatCurrency';
import { getCountryName } from '@/helpers/getCountryName';
import { calculatePreTaxMinMonthlyInstallment } from '../utils';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { useIsEmbedded } from '@/hooks/useIsEmbedded';

import { ErrorState } from './ErrorState';
import AssetDescription from './Asset/AssetDescription';
import PurchasedForChip from './PurchasedForChip';
import { ItemImage } from './ItemImage';

type CatalogItem = {
  store: Store.Catalog;
  item?: Product;
  groupNames?: string[];
};

type AssetItem = {
  store: Store.Asset;
  item?: StorefrontAsset;
  groupNames?: never;
};

type ItemDetailsModalProps = (CatalogItem | AssetItem) & {
  onAddItem: () => void;
  onRemoveItem: () => void;
  onClose: () => void;
  selected: boolean;
  isLoading?: boolean;
  errorMessage?: string;
  readOnly?: boolean;
  isEmployeeSelected?: boolean;
};

export const ItemDetailsModal = ({
  store,
  item,
  onAddItem,
  onRemoveItem,
  onClose,
  selected,
  isLoading,
  errorMessage,
  readOnly = false,
  isEmployeeSelected,
  groupNames,
}: ItemDetailsModalProps) => {
  const { t } = useTranslation('storefront');
  const { md } = useBreakpoint();
  const { techveraOutboundLocked, storefrontPayOverTime } = useFlags();

  const { isEmbedded } = useIsEmbedded();
  const { storefrontCheckoutForm } = useStorefront();
  const shoppingCountryCode =
    storefrontCheckoutForm?.getValues('shoppingCountry')?.value;

  const hasInstallmentsEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Installments
  );
  const showFinancing = storefrontPayOverTime && hasInstallmentsEntitlement;

  // Just for typescript. This component has only 1 use-case that is rendered outside the store, so the shoppingCountry could be left as "other" from a previous navigation.
  // For this case, we can consider it as undefined, because the components that use this values won't be rendered (the modal will be in "readOnly" mode).
  const finalShoppingCountryCode =
    shoppingCountryCode === 'other' ? undefined : shoppingCountryCode;

  const productPrice =
    store === Store.Catalog ? (item as Product | undefined)?.price : undefined;
  const isUnpriced = store === Store.Catalog && productPrice === undefined;
  const assetItem = store === Store.Asset ? item : undefined;
  const isAssetUnshippable = assetItem?.isShippable === false;
  const isTechveraOutboundLocked =
    techveraOutboundLocked === true &&
    assetItem?.site?.name === ELECTRIC_WAREHOUSE_SITE_NAME;
  const shouldBlockAddingToCart =
    isTechveraOutboundLocked ||
    !isEmployeeSelected ||
    isUnpriced ||
    isAssetUnshippable;

  const blockedTooltip = isTechveraOutboundLocked
    ? t('itemCard.outboundLockedTooltip')
    : isAssetUnshippable
      ? t('itemCard.notAvailableInCountry', {
          country: getCountryName(finalShoppingCountryCode),
        })
      : isUnpriced
        ? t('itemCard.unpricedTooltip')
        : t('itemCard.addToCartTooltip');

  const addToCartButtonText = isAssetUnshippable
    ? t('itemCard.notAvailableInCountry', {
        country: getCountryName(finalShoppingCountryCode),
      })
    : t('common.addToCart');

  if (isLoading && !errorMessage) {
    return (
      <ModalV2
        ariaLabelledby="specifications"
        visible
        hide={onClose}
        minHeight="30rem"
        minWidth={md ? '80rem' : undefined}>
        <ModalHeader>
          <Text id="specifications" variant="heading-2">
            {t('common.specifications')}
          </Text>
        </ModalHeader>
        <ModalBody overflowY="auto">
          <Flex
            vertical
            alignItems="center"
            width="100%"
            p="2.4rem 0"
            rowGap="2.4rem"
            data-testid="loading-item-detail">
            <Flex columnGap="3.2rem">
              <Skeleton width="25rem" height="20rem" />
              <Flex
                vertical
                rowGap="1.6rem"
                justifyContent="center"
                mt="1.6rem">
                <Skeleton width="25rem" height="4rem" />
                <Skeleton width="25rem" height="2rem" />
              </Flex>
            </Flex>
            <Flex>
              <Skeleton height="30rem" width="53.2rem" />
            </Flex>
          </Flex>
        </ModalBody>
      </ModalV2>
    );
  }

  if (!isLoading && errorMessage) {
    return (
      <ModalV2
        ariaLabelledby="specifications"
        visible
        hide={onClose}
        minHeight="30rem"
        minWidth={md ? '80rem' : undefined}>
        <ModalHeader>
          <Text id="specifications" variant="heading-2">
            {t('common.specifications')}
          </Text>
        </ModalHeader>
        <ModalBody overflowY="auto">
          <ErrorState title={t('common.error')} message={errorMessage} />
        </ModalBody>
      </ModalV2>
    );
  }

  return (
    <ModalV2
      ariaLabelledby="specifications"
      visible
      hide={onClose}
      minWidth={md ? '80rem' : undefined}>
      <ModalHeader>
        <Text id="specifications" variant="heading-2">
          {t('common.specifications')}
        </Text>
      </ModalHeader>
      <ModalBody overflowY="auto">
        {store === Store.Catalog && item && (
          <Box
            mt={spaces.space_l}
            paddingX={!md ? spaces.space_s : spaces.space_3xl}>
            <Flex
              vertical={!md}
              alignItems={!md ? 'center' : undefined}
              rowGap={spaces.space_l}
              columnGap={spaces.space_2xl}>
              <ItemImage
                flexShrink="0"
                src={item.image}
                alt={item.name}
                size="regularCardSize" // same size used on card
              />
              <Flex vertical>
                <Text title={item.name}>{item.name}</Text>
                {groupNames && groupNames.length > 0 && (
                  <Box mt={spaces.space_l}>
                    <PurchasedForChip groupNames={groupNames} />
                  </Box>
                )}
                <Flex vertical mt={spaces.space_l} rowGap={spaces.space_xs}>
                  <Text variant="heading-3">
                    {isUnpriced
                      ? t('itemCard.priceUnavailable')
                      : formatCurrency(productPrice, item.currencyCode)}
                  </Text>
                  {showFinancing && !readOnly && productPrice !== undefined && (
                    <Text variant="label-small" intent="brand">
                      {t('itemDetailsModal.orInstallmentsAsLowAs', {
                        amount: formatCurrency(
                          calculatePreTaxMinMonthlyInstallment(productPrice),
                          item.currencyCode
                        ),
                      })}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Flex>

            {item.description && (
              <Box mt={spaces.space_l}>
                <TextMarkdown specialLineBreak=";">
                  {item.description}
                </TextMarkdown>
              </Box>
            )}
          </Box>
        )}
        {store === Store.Asset && item && (
          <AssetDescription
            customAssetId={item.customAssetId}
            name={item.name}
            assetId={isEmbedded ? undefined : item.id}
            onNavigate={onClose}
            brand={item.brand}
            condition={item.condition}
            model={item.model}
            serialNumber={item.serialNumber}
            notes={item.notes}
            purchaseDate={item.purchaseDate}
            purchasePrice={item.purchasePrice}
            purchasePriceCurrencyCode={item.purchasePriceCurrencyCode}
            purchasedFromUrl={item.purchasedFromUrl}
            site={item.site}
            productType={item.productType}
            isShippable={item.isShippable}
            selfShipRequired={item.selfShipRequired}
            fullDescription
          />
        )}
      </ModalBody>
      {!readOnly && (
        <ModalFooter>
          <ModalFooterActions justifyContent={md ? 'end' : 'center'}>
            <ModalAction
              id="modal-cancel-item-button"
              onClick={() => onClose()}
              py="1.4rem"
              px="2rem"
              testId="modal-cancel-item-button"
              variant="outline"
              border="none">
              {t('common.cancel')}
            </ModalAction>
            {!selected ? (
              <Tooltip
                content={blockedTooltip}
                disabled={!shouldBlockAddingToCart}>
                <ModalAction
                  id={`${store === Store.Catalog ? 'storefront' : 'asset'}-modal-add-item-button`}
                  onClick={() => {
                    onAddItem();
                    onClose();
                  }}
                  py="1.4rem"
                  px="2rem"
                  testId="modal-add-item-button"
                  disabled={shouldBlockAddingToCart}>
                  {addToCartButtonText}
                </ModalAction>
              </Tooltip>
            ) : (
              <ModalAction
                id={`${store === Store.Catalog ? 'storefront' : 'asset'}-modal-remove-item-button`}
                onClick={() => {
                  onRemoveItem();
                  onClose();
                }}
                py="1.4rem"
                px="2rem"
                variant="outline"
                iconAfter="trashcan"
                testId="modal-remove-item-button">
                {t('common.remove')}
              </ModalAction>
            )}
          </ModalFooterActions>
        </ModalFooter>
      )}
    </ModalV2>
  );
};
